import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import GavelIcon from '@mui/icons-material/Gavel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import WorkIcon from '@mui/icons-material/Work';
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FlightIcon from '@mui/icons-material/Flight';

// Different call types - realistic for high-volume law firms across practice areas
const callTypes = [
  // Personal Injury
  { type: 'Car Accident', icon: DirectionsCarIcon, color: '#E67E22', outcome: 'booked' as const },
  { type: 'Truck Accident', icon: DirectionsCarIcon, color: '#B5683C', outcome: 'booked' as const },
  { type: 'Slip and Fall', icon: HomeIcon, color: '#9B59B6', outcome: 'booked' as const },
  { type: 'Workers Comp', icon: WorkIcon, color: '#3498DB', outcome: 'booked' as const },
  { type: 'Motorcycle Accident', icon: DirectionsCarIcon, color: '#E74C3C', outcome: 'booked' as const },
  // Criminal Defense
  { type: 'DUI Defense', icon: SecurityIcon, color: '#C0392B', outcome: 'transferred' as const },
  { type: 'Drug Charges', icon: GavelIcon, color: '#8E44AD', outcome: 'booked' as const },
  { type: 'Assault Charges', icon: SecurityIcon, color: '#2C3E50', outcome: 'transferred' as const },
  // Mass Torts
  { type: 'Mass Tort', icon: LocalHospitalIcon, color: '#16A085', outcome: 'booked' as const },
  // Family Law
  { type: 'Divorce', icon: FamilyRestroomIcon, color: '#E91E63', outcome: 'booked' as const },
  { type: 'Child Custody', icon: FamilyRestroomIcon, color: '#9C27B0', outcome: 'transferred' as const },
  // Immigration
  { type: 'Immigration', icon: FlightIcon, color: '#00BCD4', outcome: 'referral' as const },
  // Bankruptcy
  { type: 'Chapter 7 Bankruptcy', icon: AccountBalanceIcon, color: '#607D8B', outcome: 'booked' as const },
  { type: 'Chapter 13 Bankruptcy', icon: AccountBalanceIcon, color: '#795548', outcome: 'referral' as const },
  // Other high-volume
  { type: 'Wrongful Death', icon: GavelIcon, color: '#6B5F4D', outcome: 'transferred' as const },
  { type: 'Social Security Disability', icon: AccountBalanceIcon, color: '#3F51B5', outcome: 'booked' as const },
];

// Generate random caller name
const firstNames = ['Sarah', 'Michael', 'Emily', 'David', 'Lisa', 'James', 'Anna', 'Robert'];
const lastInitials = ['K', 'R', 'M', 'S', 'T', 'W', 'L', 'B'];

function getRandomCaller() {
  const first = firstNames[Math.floor(Math.random() * firstNames.length)];
  const last = lastInitials[Math.floor(Math.random() * lastInitials.length)];
  return `${first} ${last}.`;
}

// Simple waveform - pure CSS
function Waveform({ color }: { color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 18 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Box
          key={i}
          sx={{
            width: 3,
            height: '100%',
            backgroundColor: color,
            borderRadius: 1,
            animation: `wave 0.8s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            '@keyframes wave': {
              '0%, 100%': { transform: 'scaleY(0.3)' },
              '50%': { transform: 'scaleY(1)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

// Star component - simple circle that twinkles
const stars = [
  { size: 4, top: '10%', left: '10%', delay: 0 },
  { size: 3, top: '20%', left: '25%', delay: 0.3 },
  { size: 5, top: '8%', left: '45%', delay: 0.7 },
  { size: 3, top: '25%', left: '60%', delay: 0.2 },
  { size: 4, top: '15%', left: '80%', delay: 0.5 },
  { size: 3, top: '35%', left: '15%', delay: 0.8 },
  { size: 4, top: '30%', left: '35%', delay: 0.4 },
  { size: 3, top: '40%', left: '75%', delay: 0.6 },
  { size: 5, top: '12%', left: '70%', delay: 0.1 },
  { size: 3, top: '45%', left: '90%', delay: 0.9 },
];

// Night sky with stars and moon - pure CSS animations
function NightSky() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0,
        animation: 'nightSkyFade 20s ease-in-out infinite',
        '@keyframes nightSkyFade': {
          '0%, 38%, 82%, 100%': { opacity: 0 },
          '48%, 72%': { opacity: 1 },
        },
      }}
    >
      {/* Stars */}
      {stars.map((star, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: star.top,
            left: star.left,
            width: star.size,
            height: star.size,
            borderRadius: '50%',
            backgroundColor: '#FCDDA7',
            animation: `twinkle 2s ease-in-out infinite`,
            animationDelay: `${star.delay}s`,
            '@keyframes twinkle': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
              '50%': { opacity: 1, transform: 'scale(1)' },
            },
          }}
        />
      ))}
      
      {/* Moon crescent - pure inset box-shadow, no visible element */}
      <Box
        sx={{
          position: 'absolute',
          top: '12%',
          right: '15%',
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'transparent',
          boxShadow: 'inset -10px -2px 0 0 #FCDDA7',
          filter: 'drop-shadow(0 0 6px rgba(252, 221, 167, 0.4))',
        }}
      />
    </Box>
  );
}

interface CallCardData {
  id: number;
  callType: typeof callTypes[0];
  caller: string;
  offset: number;
}

// Floating call card - self-contained with its own timers
function FloatingCallCard({ 
  card, 
  onComplete 
}: { 
  card: CallCardData;
  onComplete: (id: number) => void;
}) {
  const [phase, setPhase] = React.useState<'answering' | 'answered'>('answering');
  const IconComponent = card.callType.icon;
  
  React.useEffect(() => {
    // Phase 1: Answering (waveform)
    const answerTimer = setTimeout(() => {
      setPhase('answered');
    }, 1200);
    
    // Remove after animation
    const removeTimer = setTimeout(() => {
      onComplete(card.id);
    }, 4500);
    
    return () => {
      clearTimeout(answerTimer);
      clearTimeout(removeTimer);
    };
  }, [card.id, onComplete]);

  const isAnswered = phase === 'answered';

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${50 + card.offset}%`,
        width: '88%',
        maxWidth: 300,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(8px)',
        animation: `floatUp 4.5s ease-out forwards`,
        '@keyframes floatUp': {
          '0%': { bottom: '-5%', opacity: 0, transform: 'translateX(-50%) scale(0.9)' },
          '8%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
          '85%': { opacity: 1 },
          '100%': { bottom: '100%', opacity: 0, transform: 'translateX(-50%) scale(0.95)' },
        },
      }}
    >
      {/* Status icon */}
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          backgroundColor: isAnswered ? 'rgba(39, 174, 96, 0.15)' : 'rgba(181, 104, 60, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'background-color 0.3s ease',
        }}
      >
        {isAnswered ? (
          <CheckCircleIcon sx={{ fontSize: 20, color: '#27AE60' }} />
        ) : (
          <Waveform color="#B5683C" />
        )}
      </Box>
      
      {/* Call info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <IconComponent sx={{ fontSize: 16, color: card.callType.color }} />
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#3E301E',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {card.callType.type}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
          {card.caller}
        </Typography>
      </Box>

      {/* Outcome badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: card.callType.outcome === 'booked' 
            ? 'rgba(39, 174, 96, 0.15)' 
            : card.callType.outcome === 'transferred'
            ? 'rgba(52, 152, 219, 0.15)'
            : 'rgba(156, 39, 176, 0.15)',
          border: '1px solid',
          borderColor: card.callType.outcome === 'booked'
            ? 'rgba(39, 174, 96, 0.3)'
            : card.callType.outcome === 'transferred'
            ? 'rgba(52, 152, 219, 0.3)'
            : 'rgba(156, 39, 176, 0.3)',
          opacity: isAnswered ? 1 : 0,
          transform: isAnswered ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}
      >
        {card.callType.outcome === 'booked' ? (
          <EventIcon sx={{ fontSize: 12, color: '#27AE60' }} />
        ) : (
          <PhoneForwardedIcon sx={{ fontSize: 12, color: card.callType.outcome === 'transferred' ? '#3498DB' : '#9C27B0' }} />
        )}
        <Typography 
          sx={{ 
            fontSize: '0.6rem', 
            fontWeight: 700, 
            color: card.callType.outcome === 'booked' 
              ? '#27AE60' 
              : card.callType.outcome === 'transferred' 
              ? '#3498DB' 
              : '#9C27B0',
            textTransform: 'uppercase',
          }}
        >
          {card.callType.outcome === 'booked' ? 'Booked' : card.callType.outcome === 'transferred' ? 'Transferred' : 'Referral'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AvailabilityAnimation() {
  const [activeCards, setActiveCards] = React.useState<CallCardData[]>([]);
  const [isInView, setIsInView] = React.useState(false);
  const [hasStarted, setHasStarted] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const cardIdRef = React.useRef(0);

  // Intersection Observer
  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            if (!hasStarted) setHasStarted(true);
          } else {
            setIsInView(false);
            if (hasStarted) {
              setActiveCards([]);
              setHasStarted(false);
              cardIdRef.current = 0;
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [hasStarted]);

  // Spawn call cards
  React.useEffect(() => {
    if (!isInView || !hasStarted) return;

    const spawnCard = () => {
      setActiveCards(prev => [...prev, {
        id: cardIdRef.current++,
        callType: callTypes[Math.floor(Math.random() * callTypes.length)],
        caller: getRandomCaller(),
        offset: (Math.random() - 0.5) * 24,
      }]);
    };

    spawnCard();
    const interval = setInterval(spawnCard, 900);
    return () => clearInterval(interval);
  }, [isInView, hasStarted]);

  const handleCardComplete = React.useCallback((id: number) => {
    setActiveCards(prev => prev.filter(card => card.id !== id));
  }, []);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Pure CSS background color animation - no JS needed!
        animation: hasStarted ? 'dayNightCycle 20s ease-in-out infinite' : 'none',
        backgroundColor: '#FDFCF9',
        '@keyframes dayNightCycle': {
          '0%, 100%': { backgroundColor: '#FDFCF9' },  // Day
          '25%': { backgroundColor: '#FDFCF9' },       // Day
          '35%': { backgroundColor: '#1a1a42' },       // Night
          '65%': { backgroundColor: '#1a1a42' },       // Night
          '75%': { backgroundColor: '#FDFCF9' },       // Day
        },
      }}
    >
      {/* Night sky elements - synced with background */}
      <NightSky />

      {/* Floating cards area */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', minWidth: 0 }}>
        {activeCards.map((card) => (
          <FloatingCallCard
            key={card.id}
            card={card}
            onComplete={handleCardComplete}
          />
        ))}
      </Box>
    </Box>
  );
}
